import { useEffect, useState } from 'react'
import { useNavigation, useParams, useSearchParams } from 'react-router';
import { useSpinDelay } from 'spin-delay'
import { Icon } from '#app/components/ui/icon.tsx'
import { Label } from '#app/components/ui/label'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { useSearchFilters } from '#app/contexts/filters-context.tsx';
import { useLanguage } from '#app/contexts/language-context.tsx'
import { type Filters } from '#app/routes/($lang)._search+/_layout.tsx'
import { getAreaOption, nFormatter, sendGTagEvent } from '#app/utils/misc.tsx'
import { SEARCH_AREAS } from '#app/utils/providers/autocomplete-data.ts'
import {
	SORTING,
} from '#app/utils/providers/search-filters.ts'
import {
	BATH_OPTIONS,
	BED_OPTIONS,
	PRICE_OPTIONS,
	PRICE_OPTIONS_RENT,
	PROP_TYPES_OPTIONS,
} from '#app/utils/providers/select-options.ts'
import AdvancedFiltersModal, { type AdvancedFilters } from './advanced-filters-modal'
import { SearchAreaInput } from './search-combobox/search-combobox';
import { Button } from './ui/button'

export function SearchFiltersBar({
	handleSearch,
	handleMapSearch,
}: {
	handleSearch: (filters:Filters) => void
	handleMapSearch: (filters:Filters) => void
}) {
	const transition = useNavigation()
	const isPending = useSpinDelay(transition.state === 'loading', {
		delay: 300,
		minDuration: 300,
	})
	const params = useParams();
	const [searchParams] = useSearchParams();
	let { t, isEnglish } = useLanguage();
	const { mapNavDisabled } = useSearchFilters()
	const [filters, setFilters] = useState({
		saleType: params.saleType || searchParams.get('saleType') || 'for-sale',
		propType: params.propType || searchParams.get('propType') || 'property',
		searchArea: getAreaOption(params.city || searchParams.get('city'), params.area || searchParams.get('area')),
		priceMin: searchParams.get('priceMin') || 'nomin',
		priceMax: searchParams.get('priceMax') || 'nomax',
		bedsMin: searchParams.get('bedsMin') || 'nomin',
		bedsMax: searchParams.get('bedsMax') || 'nomax',
		bathsMin: searchParams.get('bathsMin') || 'nomin',
		bathsMax: searchParams.get('bathsMax') || 'nomax',
		sorting: searchParams.get('sorting') || 'recent'
	})
	const [searchAreaInvalid, setSearchAreaInvalid] = useState(false)
	const [bedMinOptions, setBedMinOptions] = useState(BED_OPTIONS)
	const [bedMaxOptions, setBedMaxOptions] = useState(BED_OPTIONS)
	const [bathMinOptions, setBathMinOptions] = useState(BATH_OPTIONS)
	const [bathMaxOptions, setBathMaxOptions] = useState(BATH_OPTIONS)
	const [priceMinOptions, setPriceMinOptions] = useState(
		filters.saleType.includes('sale') ? PRICE_OPTIONS : PRICE_OPTIONS_RENT,
	)
	const [priceMaxOptions, setPriceMaxOptions] = useState(
		filters.saleType.includes('sale') ? PRICE_OPTIONS : PRICE_OPTIONS_RENT,
	)

	let advancedFiltersState:AdvancedFilters = {
		added: 'any',
		heating: [],
		condition: [],
		extras: []
	}

	useEffect(() => {
		setFilters({
			saleType: params.saleType || searchParams.get('saleType') || 'for-sale',
			propType: params.propType || searchParams.get('propType') || 'property',
			searchArea: getAreaOption(params.city || searchParams.get('city'), params.area || searchParams.get('area')),
			priceMin: searchParams.get('priceMin') || 'nomin',
			priceMax: searchParams.get('priceMax') || 'nomax',
			bedsMin: searchParams.get('bedsMin') || 'nomin',
			bedsMax: searchParams.get('bedsMax') || 'nomax',
			bathsMin: searchParams.get('bathsMin') || 'nomin',
			bathsMax: searchParams.get('bathsMax') || 'nomax',
			sorting: searchParams.get('sorting') || 'recent'
		})

	}, [params.saleType, params.propType, params.city, params.area, searchParams])

	useEffect(() => {
		const priceOptions = params.saleType === 'for-sale' ? PRICE_OPTIONS : PRICE_OPTIONS_RENT
		setPriceMinOptions(priceOptions)
		setPriceMaxOptions(priceOptions)
	}, [params.saleType])

	useEffect(() => {
		updateDisabledOptions("priceMin", filters.priceMin)
		updateDisabledOptions("priceMax", filters.priceMax)
		updateDisabledOptions("bedsMin", filters.bedsMin)
		updateDisabledOptions("bedsMax", filters.bedsMax)
		updateDisabledOptions("bathsMin", filters.bathsMin)
		updateDisabledOptions("bathsMax", filters.bathsMax)
	}, [])

	const updateDisabledOptions = (field: string, value: string) => {
		let options: any[] = []

		if (field.includes('price')) {
			options =
				filters.saleType.includes('sale')
					? PRICE_OPTIONS
					: PRICE_OPTIONS_RENT
		} else if (field.includes('beds')) {
			options = BED_OPTIONS
		} else if (field.includes('baths')) {
			options = BATH_OPTIONS
		}

		if (field.includes('Min')) {
			const result: any[] = []
			if (value === 'nomin') {
				if (field === 'bedsMin') {
					setBedMaxOptions(options)
				} else if (field === 'priceMin') {
					setPriceMaxOptions(options)
				} else if (field === 'bathsMin') {
					setBathMaxOptions(options)
				}
				return
			}
			const indexMin = options.findIndex((object) => object.value === value)
			if (indexMin === -1) return
			options.forEach((item, index) => {
				if (index >= indexMin) {
					result.push(item)
				}
			})
			if (field === 'bedsMin') {
				setBedMaxOptions(result)
			} else if (field === 'priceMin') {
				setPriceMaxOptions(result)
			} else if (field === 'bathsMin') {
				setBathMaxOptions(result)
			}
		} else if (field.includes('Max')) {
			const result: any[] = []
			if (value === 'nomax') {
				if (field === 'bedsMax') {
					setBedMinOptions(options)
				} else if (field === 'priceMax') {
					setPriceMinOptions(options)
				} else if (field === 'bathsMax') {
					setBathMinOptions(options)
				}
				return
			}
			const indexMax = options.findIndex((object) => object.value === value)
			if (indexMax === -1) return
			options.forEach((item, index) => {
				if (index <= indexMax) {
					result.push(item)
				}
			})
			if (field === 'bedsMax') {
				setBedMinOptions(result)
			} else if (field === 'priceMax') {
				setPriceMinOptions(result)
			} else if (field === 'bathsMax') {
				setBathMinOptions(result)
			}
		}
	}

	const handleFilterChange = (field:string, value:any) => {
		if (['bedsMin', 'bedsMax', 'priceMin', 'priceMax', 'bathsMin', 'bathsMax'].includes(field)) {
			updateDisabledOptions(field, value)
		}

		if (field === 'searchArea') {
			setSearchAreaInvalid(!value)
		}

		setFilters((prev) => ({
			...prev,
			[field]: value,
		}))

		if (field === 'sorting') {
			handleSearchInit({...filters, ...advancedFiltersState, sorting: value})
		}
	}

	const handleAdvancedFiltersSearch = (advancedFilters:AdvancedFilters) => {
		handleSearchInit({...filters, ...advancedFilters})
	}

	const handleUpdateAdvanced = (advancedFilters:AdvancedFilters) => {
		advancedFiltersState = advancedFilters;
	}

	const handleSearchInit = (filters:Filters) => {
		if (!filters.searchArea || !filters.searchArea.area || !filters.searchArea.city) {
			setSearchAreaInvalid(true)
			return;
		}
		
		setSearchAreaInvalid(false)
		// Track GA search event
		sendGTagEvent('search_search_bar', {
			searchArea: filters.searchArea.area,
            searchCity: filters.searchArea.city,
		});
		handleSearch(filters)
	}

	const resetFilters = () => {
		setFilters({
			saleType: 'for-sale',
			propType: 'property',
			searchArea: getAreaOption(params.city || searchParams.get('city'), params.area || searchParams.get('area')),
			priceMin: 'nomin',
			priceMax: 'nomax',
			bedsMin: 'nomin',
			bedsMax: 'nomax',
			bathsMin: 'nomin',
			bathsMax: 'nomax',
			sorting: 'recent'
		})
	}


	return (
		<div className="relative">
			<div className="grid grid-cols-2 gap-4 rounded-lg bg-card text-card-foreground lg:grid-cols-[repeat(10,_minmax(0,_1fr))] xl:grid-cols-[repeat(15,_minmax(0,_1fr))] lg:gap-2">
				<SearchAreaInput 
					inputClassName='h-12'
					className="col-span-2 lg:col-span-4 xl:col-span-3"
					value={filters.searchArea}
					label={t('Validation', 'Search area')}
					invalid={searchAreaInvalid}
					items={SEARCH_AREAS}
					onChange={(e) => handleFilterChange('searchArea', e.value)}
				/>

				<div className="relative lg:col-span-3 xl:col-span-2">
					<Select
						name="propType"
						value={filters.propType}
						onValueChange={(value) => handleFilterChange('propType', value)}
					>
						<SelectTrigger id="propType" className="relative h-12 px-4 py-4">
							<SelectValue placeholder="Show all" />
						</SelectTrigger>
						<SelectContent position="popper">
							<SelectItem key="property" value="property">
								{t('Validation', 'Show all')}
							</SelectItem>
							{PROP_TYPES_OPTIONS.map((item) => (
								<SelectItem key={item.value} value={item.value}>
									{isEnglish ? item.name : item.nameGR}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Label
						className="absolute left-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-background px-2 text-sm text-muted-foreground"
						htmlFor="propType"
					>
						{t('Validation', 'Property type')}
					</Label>
				</div>

				<div className="relative lg:col-span-3 xl:col-span-2">
					<Popover>
						<PopoverTrigger className="relative flex h-12 w-full items-center justify-between rounded-md border border-input bg-background px-4 py-4 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1">
							{filters.priceMax === 'nomax' && filters.priceMin === 'nomin'
								? 'Any price'
								: filters.priceMax === 'nomax'
									? `€${nFormatter(parseInt(filters.priceMin))} min`
									: filters.priceMin === 'nomin'
										? `€${nFormatter(parseInt(filters.priceMax))} max`
										: `€${nFormatter(parseInt(filters.priceMin))} - €${nFormatter(parseInt(filters.priceMax))}`}
							<Icon name="chevron-down" className="opacity-50" size="sm" />
						</PopoverTrigger>
						<Label className="absolute left-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-background px-2 text-sm text-muted-foreground">
							{t('Validation', 'Price range')}
						</Label>
						<PopoverContent className="grid w-[400px] grid-cols-2 items-center space-x-4">
							<div className="relative flex flex-col space-y-1.5">
								<Select
									name="priceMin"
									defaultValue={filters.priceMin}
									value={filters.priceMin}
									onValueChange={(value) =>
										handleFilterChange('priceMin', value)
									}
								>
									<SelectTrigger id="priceMin">
										<SelectValue placeholder="No min" />
									</SelectTrigger>
									<SelectContent position="popper">
										<SelectItem key="nomin" value="nomin">
											No min
										</SelectItem>
										{priceMinOptions.map((item) => (
											<SelectItem key={item.id} value={item.value}>
												{item.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Label
									className="absolute left-1 top-0 z-10 origin-[0] -translate-y-4 scale-75 transform bg-background px-2 text-sm text-muted-foreground"
									htmlFor="priceMin"
								>
									{t('Validation', 'Minimum price')}
								</Label>
							</div>
							<div className="relative flex flex-col space-y-1.5">
								<Select
									name="priceMax"
									defaultValue={filters.priceMax}
									value={filters.priceMax}
									onValueChange={(value) =>
										handleFilterChange('priceMax', value)
									}
								>
									<SelectTrigger id="priceMax">
										<SelectValue placeholder="No max" />
									</SelectTrigger>
									<SelectContent position="popper">
										<SelectItem key="nomax" value="nomax">
											No max
										</SelectItem>
										{priceMaxOptions.map((item) => (
											<SelectItem key={item.id} value={item.value}>
												{item.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Label
									className="absolute left-1 top-0 z-10 origin-[0] -translate-y-4 scale-75 transform bg-background px-2 text-sm text-muted-foreground"
									htmlFor="priceMax"
								>
									{t('Validation', 'Maximum price')}
								</Label>
							</div>
						</PopoverContent>
					</Popover>
				</div>

				<div className="relative lg:col-span-3 xl:col-span-2">
					<Popover>
						<PopoverTrigger className="relative flex h-12 w-full items-center justify-between rounded-md border border-input bg-background px-4 py-4 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1">
							{filters.bedsMax === 'nomax' && filters.bedsMin === 'nomin'
								? 'Any beds'
								: filters.bedsMax === 'nomax'
									? `${filters.bedsMin} beds min`
									: filters.bedsMin === 'nomin'
										? `${filters.bedsMax} beds max`
										: `${filters.bedsMin} - ${filters.bedsMax} beds`}
							<Icon name="chevron-down" className="opacity-50" size="sm" />
						</PopoverTrigger>
						<Label className="absolute left-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-background px-2 text-sm text-muted-foreground">
							{t('Validation', 'Bedrooms')}
						</Label>
						<PopoverContent className="grid w-[400px] grid-cols-2 items-center space-x-4">
							<div className="relative flex flex-col space-y-1.5">
								<Select
									name="bedsMin"
									defaultValue={filters.bedsMin}
									value={filters.bedsMin}
									onValueChange={(value) =>
										handleFilterChange('bedsMin', value)
									}
								>
									<SelectTrigger id="bedsMin">
										<SelectValue placeholder="No min" />
									</SelectTrigger>
									<SelectContent position="popper">
										<SelectItem key="nomin" value="nomin">
											No min
										</SelectItem>
										{bedMinOptions.map((item) => (
											<SelectItem key={item.id} value={item.value}>
												{item.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Label
									className="absolute left-1 top-0 z-10 origin-[0] -translate-y-4 scale-75 transform bg-background px-2 text-sm text-muted-foreground"
									htmlFor="bedsMin"
								>
									{t('Validation', 'Minimum beds')}
								</Label>
							</div>
							<div className="relative flex flex-col space-y-1.5">
								<Select
									name="bedsMax"
									defaultValue={filters.bedsMax}
									value={filters.bedsMax}
									onValueChange={(value) =>
										handleFilterChange('bedsMax', value)
									}
								>
									<SelectTrigger id="bedsMax">
										<SelectValue placeholder="No max" />
									</SelectTrigger>
									<SelectContent position="popper">
										<SelectItem key="nomax" value="nomax">
											No max
										</SelectItem>
										{bedMaxOptions.map((item) => (
											<SelectItem key={item.id} value={item.value}>
												{item.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Label
									className="absolute left-1 top-0 z-10 origin-[0] -translate-y-4 scale-75 transform bg-background px-2 text-sm text-muted-foreground"
									htmlFor="bedsMax"
								>
									{t('Validation', 'Maximum beds')}
								</Label>
							</div>
						</PopoverContent>
					</Popover>
				</div>

				<div className="relative lg:col-span-3 xl:col-span-2">
					<Popover>
						<PopoverTrigger className="relative flex h-12 w-full items-center justify-between rounded-md border border-input bg-background px-4 py-4 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1">
							{filters.bathsMax === 'nomax' && filters.bathsMin === 'nomin'
								? 'Any baths'
								: filters.bathsMax === 'nomax'
									? `${filters.bathsMin} baths min`
									: filters.bathsMin === 'nomin'
										? `${filters.bathsMax} baths max`
										: `${filters.bathsMin} - ${filters.bathsMax} baths`}
							<Icon name="chevron-down" className="opacity-50" size="sm" />
						</PopoverTrigger>
						<Label className="absolute left-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-background px-2 text-sm text-muted-foreground">
							{t('Validation', 'Bathrooms')}
						</Label>
						<PopoverContent className="grid w-[400px] grid-cols-2 items-center space-x-4">
							<div className="relative flex flex-col space-y-1.5">
								<Select
									name="bathsMin"
									defaultValue={filters.bathsMin}
									value={filters.bathsMin}
									onValueChange={(value) =>
										handleFilterChange('bathsMin', value)
									}
								>
									<SelectTrigger id="bathsMin">
										<SelectValue placeholder="No min" />
									</SelectTrigger>
									<SelectContent position="popper">
										<SelectItem key="nomin" value="nomin">
											No min
										</SelectItem>
										{bathMinOptions.map((item) => (
											<SelectItem key={item.id} value={item.value}>
												{item.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Label
									className="absolute left-1 top-0 z-10 origin-[0] -translate-y-4 scale-75 transform bg-background px-2 text-sm text-muted-foreground"
									htmlFor="bathsMin"
								>
									{t('Validation', 'Minimum baths')}
								</Label>
							</div>
							<div className="relative flex flex-col space-y-1.5">
								<Select
									name="bathsMax"
									value={filters.bathsMax}
									onValueChange={(value) =>
										handleFilterChange('bathsMax', value)
									}
								>
									<SelectTrigger id="bathsMax">
										<SelectValue placeholder="No max" />
									</SelectTrigger>
									<SelectContent position="popper">
										<SelectItem key="nomax" value="nomax">
											No max
										</SelectItem>
										{bathMaxOptions.map((item) => (
											<SelectItem key={item.id} value={item.value}>
												{item.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Label
									className="absolute left-1 top-0 z-10 origin-[0] -translate-y-4 scale-75 transform bg-background px-2 text-sm text-muted-foreground"
									htmlFor="bathsMax"
								>
									{t('Validation', 'Maximum baths')}
								</Label>
							</div>
						</PopoverContent>
					</Popover>
				</div>

				<AdvancedFiltersModal className='lg:col-span-2' isPending={isPending} handleUpdateAdvanced={handleUpdateAdvanced} handleSearch={handleAdvancedFiltersSearch} resetFilters={resetFilters}/>

				<StatusButton
					type="button"
					disabled={isPending}
					status={isPending ? 'pending' : 'idle'}
					className="flex h-12 items-center justify-center  lg:col-span-2"
					onClick={() => handleSearchInit({...filters, ...advancedFiltersState})}
				>
					<Icon name="magnifying-glass" size="lg" className="mr-1" />
					<span>{t('Common', 'Search')}</span>
				</StatusButton>
			</div>

			<div className="flex items-center justify-between flex-wrap gap-2 mt-4">
				<Button
					className="decoration-red-500 decoration-2 underline-offset-2 hover:underline"
					variant="secondary"
					onClick={() => handleMapSearch({...filters, ...advancedFiltersState})}
					disabled={mapNavDisabled}
				>
					<Icon name="location-pin" size="md" className="mr-1" />
					{t('Search', 'Map view')}
				</Button>

				<div className="relative">
					<Select
						name="sorting"
						defaultValue={filters.sorting}
						onValueChange={(value) =>
							handleFilterChange('sorting', value)
						}
					>
						<SelectTrigger
							id="sorting"
							className="h-10 px-4 py-2 bg-secondary text-secondary-foreground hover:bg-secondary/80"
						>
							<Label
								htmlFor="sorting"
								className='sr-only md:not-sr-only'
							>
								{t('Validation', 'Sort')}:&nbsp; 
							</Label>
							<SelectValue placeholder="Most recent" />
						</SelectTrigger>
						<SelectContent position="popper">
							{SORTING.map((item) => (
								<SelectItem key={item.value} value={item.value}>
									{isEnglish ? item.label : item.labelGR}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>
		</div>
	)
}
